import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Lottie from 'react-lottie-player';
import animationData from '../animations/Animation - 1742459445754.json';
import { AuthContext } from '../context/AuthContext';
import '../styles/Home.css';

const TechBadge = ({ label, color }) => (
  <span className="tech-badge" style={{ '--badge-color': color }}>{label}</span>
);

const FeatureCard = ({ icon, title, description, tag }) => (
  <div className="feature-card">
    <div className="feature-card-icon">{icon}</div>
    {tag && <span className="feature-tag">{tag}</span>}
    <h3>{title}</h3>
    <p>{description}</p>
  </div>
);

const Home = () => {
  const { user } = useContext(AuthContext);

  return (
    <div className="home-container">

      {/* ── Hero ── */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-eyebrow">Vicinity-Based Social Networking</div>
          <h1>
            Connect with <span className="highlight">Comrades</span><br />
            in Your Neighbourhood
          </h1>
          <p className="hero-subtitle">
            An AI-powered platform that links you with neighbours to share skills,
            rent items, and get things done — hyper-locally.
          </p>
          <div className="hero-buttons">
            {user ? (
              <Link to="/feed" className="cta-button primary">Go to Feed</Link>
            ) : (
              <>
                <Link to="/register" className="cta-button primary">Get Started Free</Link>
                <Link to="/login" className="cta-button secondary">Sign In</Link>
              </>
            )}
          </div>
          <div className="stat-row">
            <div className="stat-item">
              <span className="stat-number">⚡ Real-time</span>
              <span className="stat-label">Socket.io notifications</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">🤖 AI-driven</span>
              <span className="stat-label">Feed & urgency scoring</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">📍 Geo-aware</span>
              <span className="stat-label">Adjustable radius</span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <Lottie
            loop
            animationData={animationData}
            play
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="section how-it-works">
        <h2 className="section-title">How It Works</h2>
        <p className="section-subtitle">Three steps to a smarter local community</p>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-icon">📍</div>
            <div className="step-number">01</div>
            <h3>Set Your Radius</h3>
            <p>
              Register with your location and choose how far your posts should
              reach — from 500 m to several kilometres.
            </p>
          </div>
          <div className="step-connector" />
          <div className="step-card">
            <div className="step-icon">🤖</div>
            <div className="step-number">02</div>
            <h3>AI Prioritises Your Feed</h3>
            <p>
              Our Vicinity Coordinator Agent (LangGraph + Gemini) classifies
              urgency and pushes critical posts to the top instantly.
            </p>
          </div>
          <div className="step-connector" />
          <div className="step-card">
            <div className="step-icon">🤝</div>
            <div className="step-number">03</div>
            <h3>Connect &amp; Transact</h3>
            <p>
              Offer help, rent an item, or close a sale — dual-confirmation
              transactions and a reputation system keep everyone accountable.
            </p>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="section features-section">
        <h2 className="section-title">What You Can Do</h2>
        <p className="section-subtitle">Everything you need, nothing you don't</p>
        <div className="features-grid">
          <FeatureCard
            icon="🆘"
            title="Request Help"
            description="Post what you need and let nearby comrades respond with offers. NLP intent detection surfaces the most relevant replies."
            tag="NLP-powered"
          />
          <FeatureCard
            icon="🛒"
            title="Local Marketplace"
            description="List items to sell or rent. Competitive offer feeds let buyers bid transparently while you stay in full control."
          />
          <FeatureCard
            icon="🌍"
            title="Volunteer &amp; Offer Skills"
            description="Share a skill, lend a hand, or run an errand. Build your reputation score with every completed transaction."
            tag="Reputation scored"
          />
          <FeatureCard
            icon="⚡"
            title="Urgency Notifications"
            description="The AI agent scores post urgency and fires Socket.io alerts to all nearby users — no polling, instant delivery."
            tag="Real-time"
          />
          <FeatureCard
            icon="💬"
            title="Smart Comments"
            description="Comment analysis detects toxicity, intent, and even price signals — keeping discussions safe and structured."
            tag="AI moderated"
          />
          <FeatureCard
            icon="🔒"
            title="Secure Auth"
            description="JWT access tokens with refresh-token rotation. Every sensitive route is guarded; tokens auto-rotate on each response."
          />
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section className="section tech-section">
        <div className="tech-inner">
          <div className="tech-text">
            <h2>Built for Production</h2>
            <p>
              Comrades is a full-stack project that integrates geospatial databases,
              a multi-step AI agent, real-time WebSocket events, and a JWT auth
              pipeline — all deployed and running.
            </p>
            <div className="tech-groups">
              <div className="tech-group">
                <span className="tech-group-label">Frontend</span>
                <div className="tech-badges">
                  <TechBadge label="React 18" color="#61dafb" />
                  <TechBadge label="React Router v6" color="#ca4245" />
                  <TechBadge label="Socket.io-client" color="#25c2a0" />
                  <TechBadge label="Google Maps API" color="#4285f4" />
                </div>
              </div>
              <div className="tech-group">
                <span className="tech-group-label">Backend</span>
                <div className="tech-badges">
                  <TechBadge label="Node.js + Express" color="#3c873a" />
                  <TechBadge label="MongoDB Atlas" color="#47a248" />
                  <TechBadge label="Socket.io" color="#25c2a0" />
                  <TechBadge label="JWT Auth" color="#f59e0b" />
                </div>
              </div>
              <div className="tech-group">
                <span className="tech-group-label">AI / ML</span>
                <div className="tech-badges">
                  <TechBadge label="Gemini via LangChain" color="#8360c3" />
                  <TechBadge label="LangGraph Agent" color="#2ebf91" />
                  <TechBadge label="NLP Comment Analysis" color="#ef4444" />
                  <TechBadge label="$geoNear Queries" color="#f97316" />
                </div>
              </div>
            </div>
          </div>
          <div className="tech-card-stack">
            <div className="arch-card">
              <div className="arch-card-header">Vicinity Coordinator Agent</div>
              <div className="arch-card-body">
                <div className="arch-step">📩 Post created</div>
                <div className="arch-arrow">↓</div>
                <div className="arch-step">🧠 Gemini classifies urgency</div>
                <div className="arch-arrow">↓</div>
                <div className="arch-step">📍 $geoNear finds nearby users</div>
                <div className="arch-arrow">↓</div>
                <div className="arch-step">⚡ Socket.io push notification</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section cta-section">
        <div className="cta-glow" />
        <h2>Ready to meet your neighbours?</h2>
        <p>Join Comrades and start exchanging value in your vicinity today.</p>
        <div className="cta-buttons">
          {user ? (
            <Link to="/feed" className="cta-button primary large">Open My Feed</Link>
          ) : (
            <>
              <Link to="/register" className="cta-button primary large">Create Account</Link>
              <Link to="/login" className="cta-button ghost large">Sign In</Link>
            </>
          )}
        </div>
      </section>

    </div>
  );
};

export default Home;
